import dayjs from 'dayjs';

/**
 * Represents a unit of time with its divisor and threshold.
 */
interface TimeUnit {
  unit: string;
  divisor: number;
  threshold: number;
}

/**
 * Array of time units in increasing order with their corresponding thresholds.
 * The unit abbreviations are in Spanish.
 */
const timeUnits: TimeUnit[] = [
  { unit: 's', divisor: 1, threshold: 60 }, // seconds
  { unit: 'min', divisor: 60, threshold: 60 }, // minutes
  { unit: 'h', divisor: 60 * 60, threshold: 24 }, // hours
  { unit: 'd', divisor: 24 * 60 * 60, threshold: 7 }, // days
  { unit: 'sem', divisor: 7 * 24 * 60 * 60, threshold: 4 }, // weeks
  { unit: 'mes', divisor: 30 * 24 * 60 * 60, threshold: 12 }, // months
  { unit: 'año', divisor: 365 * 24 * 60 * 60, threshold: Infinity }, // years
];

/**
 * Returns a human-readable time difference from now to a given timestamp.
 * The output is in Spanish abbreviations (e.g., "2h", "3d", "Justo ahora").
 *
 * @param {string | Date} timestamp - The timestamp to compare with current time.
 * @returns {string} Time ago string in Spanish abbreviations.
 */
export const useTimeAgo = (timestamp: string | Date): string => {
  if (!timestamp) return '';

  const getTimeAgo = (timestamp: string | Date): string => {
    const currentTime = dayjs();
    const commentTime = dayjs(timestamp);
    const diffInSeconds = currentTime.diff(commentTime, 'second');

    if (Math.abs(diffInSeconds) < 60) {
      return 'Justo ahora';
    }

    for (const unit of timeUnits) {
      const diffInUnit = Math.floor(Math.abs(diffInSeconds) / unit.divisor);
      if (diffInUnit > 0 && diffInUnit < unit.threshold) {
        return `${diffInUnit} ${unit.unit}`;
      }
    }

    return 'Hace mucho tiempo';
  };

  return getTimeAgo(timestamp);
};

/**
 * Calculates the difference in minutes between now and a given date.
 *
 * @param {string} date - The date string to compare with current time.
 * @returns {number} Number of minutes between the current time and the given date.
 */
export const diffInMinutes = (date: string): number => {
  const currentTime = dayjs();
  const commentTime = dayjs(date);
  return currentTime.diff(commentTime, 'minute');
};
